import { OWNER_STAFF_ID, STAFF_COLORS, staffById } from './salonDays'
import type { StaffColor, StaffProfile } from '../types/site'

/**
 * Eén bron voor de kleur van een medewerker (`StaffProfile.color`): het blok in de agenda, het
 * rondje met initialen, de legenda en het filter. Volle klassenamen (geen samengestelde strings),
 * zodat Tailwind ze vindt. Alles binnen het palet (tailwind.config.js).
 *
 * - `dot`:   rondje met initialen; tekst haalt ≥ 4,5:1 op de vulkleur.
 * - `block`: afspraakblok (bevestigd). Licht, zodat inkt-tekst leesbaar blijft.
 * - `request`: aanvraag: gestippelde rand in haar kleur, wit vlak (nog niet zeker).
 * - `swatch`: klein bolletje in de legenda en de weekkop.
 */
export type StaffTone = { dot: string; block: string; request: string; swatch: string }

export const staffTones: Record<StaffColor, StaffTone> = {
  clay: { dot: 'bg-clay text-shell', block: 'border-clay/30 bg-blush text-ink', request: 'border-dashed border-clay bg-white text-ink', swatch: 'bg-clay' },
  sage: { dot: 'bg-sage text-shell', block: 'border-sage/35 bg-sage/15 text-ink', request: 'border-dashed border-sage bg-white text-ink', swatch: 'bg-sage' },
  rose: { dot: 'bg-rose text-ink', block: 'border-rose/60 bg-rose/25 text-ink', request: 'border-dashed border-rose bg-white text-ink', swatch: 'bg-rose' },
  ink: { dot: 'bg-ink text-shell', block: 'border-ink/25 bg-ink/[0.06] text-ink', request: 'border-dashed border-ink/60 bg-white text-ink', swatch: 'bg-ink' },
}

/** Kleur van een medewerker; onbekend of verwijderd = een vaste terugval (eigenaar clay, anders sage). */
export function staffColorOf(staff: readonly StaffProfile[], id: string | undefined): StaffColor {
  const s = staffById(staff, id)
  if (s && STAFF_COLORS.includes(s.color)) return s.color
  return !id || id === OWNER_STAFF_ID ? 'clay' : 'sage'
}

export const staffToneOf = (staff: readonly StaffProfile[], id: string | undefined): StaffTone => staffTones[staffColorOf(staff, id)]

/** De eerste kleur die nog niemand heeft, voor een nieuwe collega. Alles bezet: de minst gebruikte. */
export function nextStaffColor(staff: readonly StaffProfile[]): StaffColor {
  const free = STAFF_COLORS.find((c) => !staff.some((s) => s.color === c))
  if (free) return free
  const count = (c: StaffColor) => staff.filter((s) => s.color === c).length
  return [...STAFF_COLORS].sort((a, b) => count(a) - count(b))[0] ?? 'sage'
}

/**
 * Twee actieve medewerkers met dezelfde kleur zijn in de agenda niet uit elkaar te houden.
 * Voor de waarschuwing in Instellingen › Team. Leeg = in orde.
 */
export function clashingColors(staff: readonly StaffProfile[]): StaffColor[] {
  const active = staff.filter((s) => s.active)
  return STAFF_COLORS.filter((c) => active.filter((s) => s.color === c).length > 1)
}
